import type { GameRoom, RoomParticipant } from "../game-client/types";

/**
 * Resolve a readable display name for the given userId in a room.
 *
 * Priority:
 *   1. room.participants[userId].displayName (server-provided)
 *   2. fallback label ("플레이어" by default)
 *
 * Always avoids leaking raw userId hashes or @guest.local emails into the UI.
 */
export function getParticipantDisplayName(
  room: Pick<GameRoom, "participants"> | null | undefined,
  userId: string | null | undefined,
  fallback = "플레이어",
): string {
  if (!userId) return fallback;
  const found = room?.participants?.find((p) => p.userId === userId);
  return found?.displayName?.trim() || fallback;
}

/**
 * Map participantUserIds → display names, in input order.
 * Useful when initializing a game session that needs both arrays aligned.
 */
export function getParticipantDisplayNames(
  room:
    | Pick<GameRoom, "participants" | "participantUserIds">
    | null
    | undefined,
  fallback = "Player",
): string[] {
  if (!room) return [];
  const ids = room.participantUserIds ?? [];
  return ids.map((id) => getParticipantDisplayName(room, id, fallback));
}

export type { RoomParticipant };
